import { FormControl, FormLabel, TextField } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { TimeValidationError } from "@mui/x-date-pickers/internals/hooks/validation/useTimeValidation";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";
import { useUserProfileStore } from "../../../../App/State/Stores/UserProfileStore";
import { getDateUtc, toNearestFiveMinutes } from "../../../../MessagingCenter/Utils";

export const ScheduleTimePicker = ({
  scheduledDate,
  handleChangeCalendar,
}: {
  scheduledDate: Dayjs;
  handleChangeCalendar: (date: Dayjs | null) => void;
}) => {
  const { profile, getUserAdjustedDate, fromUserAdjustedDate } = useUserProfileStore();

  const roundedDate = useMemo(() => {
    const rounded = toNearestFiveMinutes(scheduledDate);
    if (rounded.minute() !== scheduledDate.minute()) {
      return getUserAdjustedDate(rounded).utc();
    }
    return getUserAdjustedDate(scheduledDate).utc();
  }, [scheduledDate, getUserAdjustedDate]);

  const [error, setError] = useState<TimeValidationError | null>(null);

  const errorMessage = useMemo(() => {
    switch (error) {
      case "invalidDate": {
        return "Your date is not valid";
      }

      default: {
        return null;
      }
    }
  }, [error]);

  const handleInputTimePicker = useDebouncedHandler(
    (value: Dayjs | null | undefined, keyboardInputValue?: string | undefined) => {
      const time = value ? value : keyboardInputValue ? dayjs(keyboardInputValue) : null;

      if (time) {
        // convert from utc to user time
        value = fromUserAdjustedDate(time);

        if (value.isValid()) {
          handleChangeCalendar(value.second(0).millisecond(0));
        }
      }
    },
    50,
  );

  return (
    <FormControl required aria-required aria-label="Select send time" fullWidth>
      <FormLabel id="select-time-label" htmlFor="select-send-time">
        Time ({getDateUtc(roundedDate).format("h:mm a")})
      </FormLabel>
      <TimePicker
        value={roundedDate}
        onChange={handleInputTimePicker}
        minutesStep={5}
        onError={(err) => setError(err)}
        InputAdornmentProps={{
          id: "select-time-adornment",
        }}
        renderInput={(params) => (
          <TextField
            error={errorMessage !== null}
            id="select-send-time"
            {...params}
            helperText={errorMessage ? errorMessage : profile.timezoneId}
            sx={{
              position: "relative",
              "& .MuiFormHelperText-root": {
                position: "absolute",
                top: "100%",
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};

export type MinMaxTime = {
  minTime: Dayjs;
  maxTime: Dayjs;
};
